import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Trash2 } from 'lucide-react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { Tabs, TabsList, TabsTrigger, Input, Button } from '@ideoz/ui';
import { terminalStore, clearTerminal, type TerminalLine } from '../stores/terminal';
import { workbenchStore } from '../stores/workbench';
import { startInteractiveShell, type InteractiveShell } from '../webcontainer/index';
import { stripAnsi } from '../runtime/tool-adapters/build';
import '@xterm/xterm/css/xterm.css';

const XTERM_THEME_DARK: NonNullable<ConstructorParameters<typeof Terminal>[0]>['theme'] = {
  background: '#1e1e1e',
  foreground: '#d4d4d4',
  cursor: '#d4d4d4',
  selectionBackground: '#264f78',
  black: '#1e1e1e',
  red: '#f44747',
  green: '#608b4e',
  yellow: '#b58900',
  blue: '#2563eb',
  magenta: '#9333ea',
  cyan: '#0d9488',
  white: '#d4d4d4',
  brightBlack: '#808080',
  brightRed: '#f44747',
  brightGreen: '#4d7c0f',
  brightYellow: '#a16207',
  brightBlue: '#1d4ed8',
  brightMagenta: '#9333ea',
  brightCyan: '#0f766e',
  brightWhite: '#ffffff',
};

const XTERM_BASE = {
  fontSize: 13,
  fontFamily: 'Menlo, Monaco, "Cascadia Code", "Courier New", monospace',
  lineHeight: 1.25,
  convertEol: true,
};

function safeFit(term: Terminal, fitAddon: FitAddon, container: HTMLElement | null): void {
  if (!container) return;
  if (container.offsetWidth < 2 || container.offsetHeight < 2) return;
  const core = (term as unknown as { _core?: { _renderService?: unknown } })._core;
  if (!core?._renderService) return;
  try {
    fitAddon.fit();
  } catch {
    // dispose race or layout thrash
  }
}

/**
 * Logs use a plain scroll view instead of xterm: xterm always paints a full row grid
 * (one fixed-height div per terminal row), so sparse output leaves huge empty bands.
 */
function OutputPanel({ visible, lines }: { visible: boolean; lines: TerminalLine[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const onScroll = (): void => {
    const el = scrollerRef.current;
    if (!el) return;
    const threshold = 80;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  useLayoutEffect(() => {
    if (!visible) return;
    const el = scrollerRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines, visible]);

  return (
    <div
      className="absolute inset-0 bg-[#fafafa] px-3 pt-2 pb-2 flex flex-col min-h-0"
      style={{ display: visible ? 'flex' : 'none' }}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain rounded-sm border border-border/30 bg-[#fafafa] px-2 py-1.5"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {/* No flex-1 on inner — it capped height and prevented overflow/scrolling */}
        <div className="flex min-h-full flex-col justify-end py-0.5">
          <div
            className="font-mono text-[13px] leading-tight text-[#1e293b] flex flex-col gap-0"
            style={{ fontFamily: XTERM_BASE.fontFamily, lineHeight: 1.35 }}
          >
            {lines.map((line, i) => (
              <div
                key={`${i}-${line.type}-${line.text.slice(0, 48)}`}
                className={
                  line.type === 'info'
                    ? 'whitespace-pre-wrap break-words text-[#0d9488]'
                    : line.type === 'stderr'
                      ? 'whitespace-pre-wrap break-words text-[#b91c1c]'
                      : 'whitespace-pre-wrap break-words text-[#1e293b]'
                }
              >
                {line.type === 'info' ? line.text : stripAnsi(line.text)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShellPanel({ visible }: { visible: boolean }) {
  const termRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const shellRef = useRef<InteractiveShell | null>(null);
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  const webcontainer = useStore(workbenchStore.webcontainer);

  useEffect(() => {
    if (!visible) return;
    const term = xtermRef.current;
    const fitAddon = fitAddonRef.current;
    if (!term || !fitAddon) return;
    requestAnimationFrame(() => {
      safeFit(term, fitAddon, termRef.current);
      if (shellRef.current) shellRef.current.resize(term.cols, term.rows);
    });
  }, [visible]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      void shellRef.current?.kill().catch(() => {});
      xtermRef.current?.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
      shellRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!visible || initializedRef.current || !termRef.current || !webcontainer) return;
    initializedRef.current = true;

    const el = termRef.current;
    const term = new Terminal({
      ...XTERM_BASE,
      theme: XTERM_THEME_DARK,
      disableStdin: false,
      cursorBlink: true,
    });
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(el);
    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    const scheduleFit = () => {
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        safeFit(term, fitAddon, el);
      });
    };
    scheduleFit();
    scheduleFit();

    const ro = new ResizeObserver(() => {
      if (!mountedRef.current) return;
      requestAnimationFrame(() => {
        safeFit(term, fitAddon, el);
        shellRef.current?.resize(term.cols, term.rows);
      });
    });
    ro.observe(el);

    term.writeln('\x1b[36mInitializing shell…\x1b[0m');
    requestAnimationFrame(() => {
      if (!mountedRef.current) return;
      safeFit(term, fitAddon, el);

      startInteractiveShell(webcontainer, {
        cols: term.cols || 80,
        rows: term.rows || 24,
        onData: (chunk) => {
          if (mountedRef.current) term.write(chunk);
        },
        onExit: () => {
          if (!mountedRef.current) return;
          term.writeln('\r\n\x1b[33mShell exited. Reopen the terminal to start a new session.\x1b[0m');
          initializedRef.current = false;
        },
      })
        .then((shell) => {
          if (!mountedRef.current) {
            void shell.kill();
            return;
          }
          shellRef.current = shell;
          term.onData((data) => shell.write(data));
        })
        .catch((err: unknown) => {
          term.writeln(`\x1b[31mFailed to start shell: ${String(err)}\x1b[0m`);
          initializedRef.current = false;
        });
    });

    return () => {
      ro.disconnect();
    };
  }, [visible, webcontainer]);

  return (
    <div
      className="absolute inset-0 bg-[#1e1e1e] px-3 pt-2 pb-2 flex flex-col min-h-0"
      style={{ display: visible ? 'flex' : 'none' }}
    >
      <div ref={termRef} className="h-full w-full flex-1 min-h-0 min-w-0" />
    </div>
  );
}

export function TerminalTabs() {
  const [activeTab, setActiveTab] = useState<'logs' | 'terminal'>('logs');
  const [logFilter, setLogFilter] = useState('');
  const rawLines = useStore(terminalStore.lines);

  const filteredLines = useMemo(() => {
    const q = logFilter.trim().toLowerCase();
    if (!q) return rawLines;
    return rawLines.filter((l) => l.text.toLowerCase().includes(q));
  }, [rawLines, logFilter]);

  return (
    <div className="h-full min-h-0 flex flex-col bg-background/80 backdrop-blur-md">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border/50 shrink-0 glass-subtle">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'logs' | 'terminal')}
          className="flex-1 min-w-0"
        >
          <TabsList className="h-9 bg-muted/50 border border-border/40 p-0.5">
            <TabsTrigger value="logs" className="text-xs px-3 data-[state=active]:shadow-sm rounded-md">
              Logs
            </TabsTrigger>
            <TabsTrigger value="terminal" className="text-xs px-3 data-[state=active]:shadow-sm rounded-md">
              Terminal
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === 'logs' && (
          <div className="flex items-center gap-1 shrink-0">
            <Input
              placeholder="Filter…"
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)}
              className="h-8 w-[140px] sm:w-44 text-xs font-mono"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground"
              onClick={() => clearTerminal()}
              title="Clear logs"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 relative min-w-0">
        <OutputPanel visible={activeTab === 'logs'} lines={filteredLines} />
        <ShellPanel visible={activeTab === 'terminal'} />
      </div>
    </div>
  );
}
